import { PoweredByCuddles } from 'components/Cuddles/PoweredBy/PoweredByCuddles';
import { LeftColumn } from 'components/Layout/TwoColumns/LeftColumn/LeftColumn';
import { RightColumn } from 'components/Layout/TwoColumns/RightColumn/RightColumn';
import { TwoColumnsLayout } from 'components/Layout/TwoColumns/TwoColumnsLayout';
import { MarketplaceInfo } from 'components/Marketplace/Info/MarketplaceInfo';
import { PetForm } from 'components/Pet/Form/PetForm';
import { useMarketplace } from 'hooks/useMarketplace';
import { useOnlineBookingState } from 'hooks/useOnlineBookingState';
import React from 'react';
import { dispatch } from 'store';
import { Pet } from 'types/Pet';
import { navigateToStep } from 'actions/onlineBooking/actions/OnlineBookingFlowActions';
import { saveOnlineBookingPetThunk } from 'actions/onlineBooking/actions/OnlineBookingPetActions';
import { OnlineBookingFooter } from 'components/OnlineBooking/Footer/OnlineBookingFooter';
import { OnlineBookingHeader } from 'components/OnlineBooking/Header/OnlineBookingHeader';
import { OnlineBookingResume } from 'components/OnlineBooking/Resume/OnlineBookingResume';
import { OnlineBookingStep } from 'types/OnlineBooking';

export const OnlineBookingCreatePetStep: React.FC = () => {
    const marketplace = useMarketplace();
    const { customer, isReturningClient } = useOnlineBookingState();

    const formRef = React.useRef<{ submit: () => void }>(null);

    const [isLoading, setIsLoading] = React.useState(false);

    const triggerSubmit = () => {
        if (formRef.current) {
            formRef.current.submit();
        }
    };

    const submitHandler = async (petData: Pet) => {
        setIsLoading(true);

        try {
            await dispatch(saveOnlineBookingPetThunk(petData));

            dispatch(navigateToStep(OnlineBookingStep.PetList));
        } catch {
            // The error is already surfaced from the save flow (a vaccine record
            // failure opens its modal); keep the form open and usable so the
            // customer can fix the submission.
        } finally {
            setIsLoading(false);
        }
    };

    const backHandler = React.useCallback(() => {
        dispatch(navigateToStep(OnlineBookingStep.Signup));
    }, []);

    return (
        <TwoColumnsLayout
            header={<OnlineBookingHeader onBack={isReturningClient ? undefined : backHandler} />}
        >
            <LeftColumn contentClassName="flex flex-col flex-1">
                <h1 className="h2 underlined w-full">Add pet</h1>

                {customer.id && (
                    <PetForm ref={formRef} enforceRequirements onSubmit={submitHandler} />
                )}

                <div className="mt-auto hide-on-mobile-and-tablet pt-6">
                    <PoweredByCuddles />
                </div>
            </LeftColumn>

            <RightColumn
                poweredByCuddles
                footer={
                    <OnlineBookingFooter
                        loading={isLoading}
                        disabled={isLoading}
                        onSubmit={triggerSubmit}
                    />
                }
            >
                <div className="flex flex-col gap-5 hide-on-mobile-and-tablet">
                    <MarketplaceInfo inverted marketplace={marketplace} />

                    <OnlineBookingResume />
                </div>
            </RightColumn>
        </TwoColumnsLayout>
    );
};
